import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Divider, Row, Select } from 'antd';
import { DEBOUNCE_TIME } from '@/std/const/debounceTime';
import { filterOption } from '@/std/filter';
import { useDebouncedState } from '@/std/hooks/useDebouncedState';
import { randomColorHex } from '@/std/random';
import type { ProdutoVariacao } from '../types/ProdutoVariacao';
import {
    apiVariacaoConsultarQueryOptions,
    type ApiVariacaoConsultarParams,
} from '../api/apiVariacaoConsultar';
import { useMutationVariacaoInserir } from '../api/apiVariacaoInserir';

export function ProdutoVariacaoSelect({
    value,
    onChange,
    filter,
}: {
    value?: number | null;
    onChange?: (idpk: number | null, variacao: ProdutoVariacao | null) => void;
    filter?: string[];
}) {
    const mutationInserir = useMutationVariacaoInserir();

    const [search, setSearch] = useDebouncedState('', DEBOUNCE_TIME);

    const params: ApiVariacaoConsultarParams = {
        orderby: 'var_nome',
        qtde_registros: 10,
        registro_inicial: 0,
        variacao: search,
        where: search === '' && value ? `var_idpk=${value}` : undefined,
    };

    const { data, isFetching } = useQuery(apiVariacaoConsultarQueryOptions(params));

    const registros = data?.registros ?? [];

    const options = (
        filter ? registros.filter((item) => !filter.includes(item.var_nome)) : registros
    ).map((item) => ({
        label: item.var_nome,
        value: item.var_idpk,
    }));

    const handleChange = (newValue: number | undefined | null): void => {
        if (!onChange) {
            return;
        }

        const obj = registros.find((item) => item.var_idpk === newValue);
        onChange(newValue || null, obj || null);
    };

    const handleSearch = (value: string): void => {
        setSearch(value);
    };

    const adicionar = async (): Promise<void> => {
        await mutationInserir.mutateAsync({
            valor: [],
            var_cor: randomColorHex(),
            var_descricao: '',
            var_nome: search,
        });
    };

    const popupRender = (menu: React.ReactElement): React.ReactElement => (
        <div className='w-full overflow-visible bg-white'>
            {menu}

            {!isFetching && search.length > 2 && (
                <>
                    <Divider style={{ margin: '4px 0' }} />

                    <Row justify='center'>
                        <Button
                            disabled={!search || search.length < 3}
                            icon={<PlusOutlined />}
                            loading={mutationInserir.isPending}
                            onClick={adicionar}
                            style={{ color: 'white' }}
                            tabIndex={0}
                            type='primary'
                        >
                            Adicionar nova variação
                        </Button>
                    </Row>
                </>
            )}
        </div>
    );

    return (
        <Select
            allowClear={true}
            className='w-full'
            loading={isFetching || mutationInserir.isPending}
            notFoundContent={isFetching ? 'Pesquisando...' : undefined}
            onChange={handleChange}
            options={options}
            popupRender={popupRender}
            showSearch={{ filterOption, onSearch: handleSearch }}
            value={value}
        />
    );
}
